import { useIonModal } from "@ionic/react";
import { ReactNode, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import styles from "@components/DeleteConfirmationAlert.module.css";
import FormButtons from "@components/fields/FormButtons";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import { Opener } from "@hooks/useOpener";

type Props = {
  cancelKey?: string;
  deleteKey?: string;
  extraContent?: ReactNode;
  headerKey?: string;
  messageKey?: string;
  messageValue?: string | null;
  messageValueKey?: string;
  onCancel?: () => void;
  onDelete: () => void;
  opener: Opener;
  skipCloseOnDelete?: boolean;
};

type ModalBodyProps = {
  cancelKey: string;
  deleteKey: string;
  extraContent?: ReactNode;
  headerKey: string;
  messageKey?: string;
  messageValue: string | null | undefined;
  messageValueKey: string | undefined;
  onCancel: () => void;
  onDelete: () => void;
};

const ModalBody = ({
  cancelKey,
  deleteKey,
  extraContent,
  headerKey,
  messageKey,
  messageValue,
  messageValueKey,
  onCancel,
  onDelete
}: ModalBodyProps) => (
  <div className={styles.modalInsides} data-test="delete-confirmation-alert">
    <div className={styles.headingSpacing}>
      <Heading2>
        <FormattedMessage id={headerKey} />
      </Heading2>
    </div>

    <BodyCopy>
      {messageValueKey && messageValue ? (
        <FormattedMessage id={messageKey} values={{ [messageValueKey]: messageValue }} />
      ) : messageKey ? (
        <FormattedMessage id={messageKey} />
      ) : null}
    </BodyCopy>
    {extraContent}
    <div className={styles.buttons}>
      <FormButtons
        cancelKey={cancelKey}
        narrowMargin
        onCancel={onCancel}
        onSave={onDelete}
        saveKey={deleteKey}
        skipValid
      />
    </div>
  </div>
);

const DeleteConfirmationAlert = (props: Props) => {
  const {
    cancelKey = "dictionary.cancel",
    deleteKey = "dictionary.delete",
    extraContent,
    headerKey = "dictionary.reallyDelete",
    messageKey,
    messageValue,
    messageValueKey,
    onCancel,
    onDelete,
    opener,
    skipCloseOnDelete = false
  } = props;

  const [presentModal, dismissModal] = useIonModal(ModalBody, {
    cancelKey,
    deleteKey,
    extraContent,
    headerKey,
    messageKey,
    messageValue,
    messageValueKey,
    onCancel: onCancel ?? opener.close,
    onDelete: () => {
      if (!skipCloseOnDelete) {
        opener.close();
      }
      onDelete();
    }
  });

  useEffect(() => {
    if (opener.isOpen) {
      presentModal({
        backdropDismiss: false,
        cssClass: "fitToContent",
        onDidDismiss: opener.close
      });
    } else {
      dismissModal();
    }
  }, [dismissModal, opener.close, opener.isOpen, presentModal]);

  return null;
};

export default DeleteConfirmationAlert;
